import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';


// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const AccountDeletionContent: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Validate email format
      if (!EMAIL_REGEX.test(email)) {
        setError(t('account_deletion.error.invalid_email') as string);
        return;
      }

      // Insert request into Supabase
      const { error: supabaseError } = await supabase
        .from('account_deletion_request')
        .insert([
          { 
            email
          }
        ]);

      if (supabaseError) {
        // Check if it's a unique constraint error
        if (supabaseError.code === '23505') {
          setError(t('account_deletion.error.already_requested') as string);
          return;
        }
        throw supabaseError;
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting deletion request:', err);
      setError(t('account_deletion.error.submission_failed') as string);
    }
  };

  if (isSubmitted) {
    return (
      <main className="account-deletion-page">
        <div className="bg-white border-4 border-black p-8 transform translate-x-1 translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h1 className="text-4xl font-bold mb-6">{t('account_deletion.success.title')}</h1>
          <div className="bg-green-50 border-4 border-green-600 p-6">
            <p className="text-green-800 text-lg">{t('account_deletion.success.message')}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="account-deletion-page">
      <div style={{
        backgroundColor: 'white',
        border: 'var(--border-thick)',
        padding: '2rem',
        transform: 'translate(4px, 4px)',
        boxShadow: 'var(--box-shadow)'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontFamily: 'var(--font-heading)',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          {t('account_deletion.title')}
        </h1>
        
        <div style={{ 
          backgroundColor: 'var(--background-color)',
          border: 'var(--border-thick)',
          padding: '1.5rem',
          marginBottom: '3rem'
        }}>
          <p style={{ 
            fontFamily: 'var(--font-body)',
            fontSize: '1.125rem',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            {t('account_deletion.warning')}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} style={{ textAlign: 'center' }}>
          <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
            <label 
              htmlFor="email" 
              style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '1.125rem',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}
            >
              {t('account_deletion.email_label')}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: 'var(--border-thick)',
                fontFamily: 'var(--font-body)',
                fontSize: '1.125rem',
                textAlign: 'center',
                marginBottom: '2rem'
              }}
              placeholder={t('account_deletion.email_placeholder') as string}
              required
            />
          </div>

          {error && (
            <div style={{
              backgroundColor: 'var(--background-color)',
              border: 'var(--border-thick)',
              padding: '1rem',
              maxWidth: '28rem',
              margin: '0 auto 2rem'
            }}>
              <p style={{ 
                color: 'var(--error-color)',
                fontFamily: 'var(--font-body)',
                fontSize: '1.125rem',
                textAlign: 'center'
              }}>
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            style={{
              width: '100%',
              maxWidth: '28rem',
              backgroundColor: 'var(--secondary-color)',
              color: 'white',
              padding: '1rem 1.5rem',
              border: 'var(--border-thick)',
              fontFamily: 'var(--font-body)',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginTop: '2rem'
            }}
          >
            {t('account_deletion.submit_button')}
          </button>
        </form>
      </div>
    </main>
  );
};

export default AccountDeletionContent; 